'use client';

import { useSyncExternalStore } from 'react';
import type { ResolvedSelectedOption } from '../components/seedingDetail/adapters/seedingProductOptionsAdapter';
import type { SeedingContentType } from '../components/seedingDetail/types';

/**
 * 시딩 신청/수락 화면의 선택 초안(옵션·콘텐츠 유형) — campaignId 키 client store.
 *
 * 신청 도중 SNS 채널 등록 등 별도 라우트로 이동했다 돌아오면 page 컴포넌트가 언마운트→리마운트되어
 * 로컬 useState 가 초기화된다. 같은 모듈 인스턴스를 공유하는 store 로 끌어올려 라우트 왕복 간 유지한다.
 * (배송지는 `useSelectedDeliveryAddress` + 서버 영속(PUT)으로 별도 유지.) (SID-4114)
 *
 * NOTE(SID-4114 재발): 무신사 SNS 등록(`/curator/profile-edit/sns/:slug`)은 curator 앱 SPA 라우트가
 * 아니라 무신사 본 앱으로의 cross-app hard navigation 이라, in-memory 모듈 Map 만으로는 페이지가
 * 통째로 reload 되며 초안이 소실됐다. 같은 탭에서 살아남도록 sessionStorage 로 write-through 영속한다
 * (탭 닫으면 정리). 29CM(`/profile/sns`, SPA)에서도 무해.
 */
export interface SeedingSelectionDraft {
  /** goodsNo → 신청 payload 용 해석된 옵션. */
  optionsByGoodsNo: Record<number, ResolvedSelectedOption>;
  /** goodsNo → 상품 카드 표시용 옵션 라벨. */
  optionOverrides: Record<number, string>;
  /** 선택한 콘텐츠 유형 (editable 캠페인). */
  contentType: SeedingContentType | null;
  /** 브랜드에 전달할 free-text 메시지. 미입력은 null. (SID-4157) */
  message: string | null;
}

const EMPTY_DRAFT: SeedingSelectionDraft = {
  optionsByGoodsNo: {},
  optionOverrides: {},
  contentType: null,
  message: null,
};

const store = new Map<number, SeedingSelectionDraft>();
/** sessionStorage 하이드레이션을 campaignId 당 1회만 시도하도록 기록. */
const hydrated = new Set<number>();
const listeners = new Set<() => void>();
const emit = (): void => {
  for (const listener of listeners) listener();
};

const STORAGE_KEY_PREFIX = 'seeding-selection-draft:';
const storageKey = (campaignId: number): string => `${STORAGE_KEY_PREFIX}${campaignId}`;

/** 라우트 reload(무신사 cross-app SNS 등록 등) 간 보존을 위해 sessionStorage 에 write-through. */
const persist = (campaignId: number, draft: SeedingSelectionDraft): void => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(storageKey(campaignId), JSON.stringify(draft));
  } catch {
    // 용량 초과/프라이빗 모드 등 — 영속 실패해도 in-memory store 로 동작.
  }
};

/**
 * getSnapshot 은 안정적 참조를 돌려줘야 한다(매 호출 새 객체 → 무한 렌더). 따라서 sessionStorage
 * 하이드레이션은 campaignId 당 1회만 수행해 결과를 Map 에 캐시하고, 이후엔 같은 참조를 반환한다.
 * 값이 없으면 싱글턴 EMPTY_DRAFT 를 반환(Map 에 넣지 않음 → 참조 안정).
 */
const read = (campaignId: number): SeedingSelectionDraft => {
  const cached = store.get(campaignId);
  if (cached) return cached;
  if (!hydrated.has(campaignId) && typeof window !== 'undefined') {
    hydrated.add(campaignId);
    try {
      const raw = window.sessionStorage.getItem(storageKey(campaignId));
      if (raw) {
        const parsed = { ...EMPTY_DRAFT, ...(JSON.parse(raw) as Partial<SeedingSelectionDraft>) };
        store.set(campaignId, parsed);
        return parsed;
      }
    } catch {
      // 파싱 실패 — EMPTY_DRAFT 로 진행.
    }
  }
  return EMPTY_DRAFT;
};

/** 초안 1건 확정 — in-memory 갱신 + sessionStorage write-through + 구독자 알림. */
const commit = (campaignId: number, draft: SeedingSelectionDraft): void => {
  store.set(campaignId, draft);
  persist(campaignId, draft);
  emit();
};

/** 옵션 선택 1건 갱신 (payload 옵션 + 표시 라벨 동시). */
export const setSeedingDraftOption = (
  campaignId: number,
  goodsNo: number,
  option: ResolvedSelectedOption,
  label: string,
): void => {
  const current = read(campaignId);
  commit(campaignId, {
    ...current,
    optionsByGoodsNo: { ...current.optionsByGoodsNo, [goodsNo]: option },
    optionOverrides: { ...current.optionOverrides, [goodsNo]: label },
  });
};

/**
 * 슬롯(아더컬러) 옵션 선택 — 슬롯의 다른 멤버 색상을 먼저 비우고 선택분만 남긴다.
 * (draft 는 goodsNo 키라, 같은 슬롯에서 다른 색을 재선택하면 이전 색 entry 가 잔존하기 때문.)
 */
export const setSeedingDraftSlotOption = (
  campaignId: number,
  slotMemberGoodsNos: number[],
  goodsNo: number,
  option: ResolvedSelectedOption,
  label: string,
): void => {
  const current = read(campaignId);
  const optionsByGoodsNo = { ...current.optionsByGoodsNo };
  const optionOverrides = { ...current.optionOverrides };
  for (const memberGoodsNo of slotMemberGoodsNos) {
    delete optionsByGoodsNo[memberGoodsNo];
    delete optionOverrides[memberGoodsNo];
  }
  optionsByGoodsNo[goodsNo] = option;
  optionOverrides[goodsNo] = label;
  commit(campaignId, { ...current, optionsByGoodsNo, optionOverrides });
};

/** 콘텐츠 유형 갱신. */
export const setSeedingDraftContentType = (
  campaignId: number,
  contentType: SeedingContentType | null,
): void => {
  const current = read(campaignId);
  commit(campaignId, { ...current, contentType });
};

/** 브랜드 전달 메시지(free-text) 갱신 — SNS 채널 등록 등 라우트 왕복 간 보존. (SID-4157) */
export const setSeedingDraftMessage = (campaignId: number, message: string): void => {
  const current = read(campaignId);
  commit(campaignId, { ...current, message });
};

/**
 * 초안 1건 정리 — 신청/수락 성공 등 확정 이탈 시점에 호출. in-memory + sessionStorage 제거.
 * (왕복 보존은 유지하되, 흐름을 확정적으로 떠날 때만 비운다 — QAT-39457)
 */
export const clearSeedingDraft = (campaignId: number): void => {
  store.delete(campaignId);
  hydrated.delete(campaignId);
  if (typeof window !== 'undefined') {
    try {
      window.sessionStorage.removeItem(storageKey(campaignId));
    } catch {
      // 영속 제거 실패해도 in-memory 는 비웠으므로 무해.
    }
  }
  emit();
};

/**
 * 모든 시딩 초안 정리 — 시딩 PLP(협업하기) 진입 시 호출. 신청/수락 도중 뒤로가기로 목록까지
 * 이탈하면 재진입에서 이전 선택값이 남지 않도록 전부 비운다. (QAT-39457)
 * SNS등록/콘텐츠기간/배송지 왕복은 PLP 를 거치지 않으므로 보존이 유지된다.
 */
export const clearAllSeedingDrafts = (): void => {
  store.clear();
  hydrated.clear();
  if (typeof window !== 'undefined') {
    try {
      const keys: string[] = [];
      for (let i = 0; i < window.sessionStorage.length; i += 1) {
        const key = window.sessionStorage.key(i);
        if (key?.startsWith(STORAGE_KEY_PREFIX)) keys.push(key);
      }
      for (const key of keys) window.sessionStorage.removeItem(key);
    } catch {
      // 영속 제거 실패해도 in-memory 는 비웠으므로 무해.
    }
  }
  emit();
};

export const useSeedingSelectionDraft = (campaignId: number): SeedingSelectionDraft =>
  useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    () => read(campaignId),
    () => EMPTY_DRAFT,
  );
